import * as WrapBalanced from "react-wrap-balancer"
import { JSXElementConstructor, PropsWithChildren, useEffect } from "react"
import { BrowserRouter } from "react-router-dom"
import "sendbeacon-polyfill"
import { polyfill as smoothscroll } from "smoothscroll-polyfill"
import { useScrollbarWidth } from "react-use"
import { toast, ToastContainer } from "react-toastify"
import * as React from "react"
import { createPortal } from "react-dom"
import { Provider } from "react-redux"

import { CallNotification } from "./modules/Call/components"
import { BrowserCheck, ErrorBoundary } from "./components/ErrorBoundary"

import { useIsSSR } from "utils/hooks/useIsSSR"
import { AppRouter } from "app/AppRouter/AppRouter"
import { NotificationBanner } from "components/NotificationBanner/NotificationBanner"
import { ClientProvider } from "modules/ClientAuthorization/common/ClientContext"
import { ReatomContextProvider } from "modules/PsyCatalog/common/ctx"
import { Compose } from "utils/helpers/Compose"
import { useDidMount } from "utils/customHooks"
import { UTM_KEY } from "utils/utm"
import { AppStatusProvider } from "utils/AppStatusContext"
import { UserAccount } from "utils/UserAccount"
import { IS_PRODUCTION } from "utils/env-management"
import { isBrowser } from "utils/infrastructure/ssg-utils"
import { AppQueryClientProvider } from "utils/infrastructure/AppQueryClientProvider"
import { GrowthBookProvider, useGrowthBook } from "utils/infrastructure/growthBook"
import { GA_CLIENT_ID_DIMENSION } from "utils/analytics/funnels"
import { gaGetClientIdSync, gaSetUserProperty, initGtagCtx } from "utils/analytics/google-analytics"
import "utils/ThirdParty/Sentry/init"
import { store } from "utils/store/store"

function saveUtmParams() {
    const url = new URL(window.location.href)
    let utms = ""
    url.searchParams.forEach((value, key) => {
        if (key.startsWith("utm")) {
            utms += `${key}=${value}|||`
        }
    })
    if (utms.length === 0) {
        return
    }
    document.cookie = `${UTM_KEY}=${utms};path=/`
    document.cookie = `${UTM_KEY}=${utms};domain=.${window.location.host};path=/`
}

saveUtmParams()

smoothscroll()

const searchParams = new URL(window.location.href).searchParams
const userName = searchParams.get("user_name")
const userEmail = searchParams.get("user_email")
const userPhone = searchParams.get("user_phone")
const assumeSignedIn = searchParams.get("assume_signed_in")

if (userName && userEmail) {
    UserAccount.storeSignIn({
        name: userName,
        email: userEmail,
        phone: userPhone || undefined,
    })
} else if (assumeSignedIn !== null) {
    if (assumeSignedIn === "0") {
        UserAccount.signOut()
    } else if (!IS_PRODUCTION) {
        UserAccount.storeSignIn({
            name: "Developer Test",
            email: "test@test.com",
        })
    }
}

const AppRoot = () => {
    const isSSR = useIsSSR()

    useGrowthBook()

    useDidMount(() => {
        void initGtagCtx().then(() => {
            const clientId = gaGetClientIdSync()
            clientId && gaSetUserProperty(GA_CLIENT_ID_DIMENSION, () => clientId)
        })
    })
    /**
     * Sets css variable window-height to actual window height
     * for compability with mobile browsers.
     * https://dev.to/nirazanbasnet/dont-use-100vh-for-mobile-responsive-3o97
     */
    useDidMount(() => {
        if (!isBrowser()) return
        const setWindowHeightInCSS = () => {
            document.documentElement.style.setProperty("--window-height", `${window.innerHeight}px`)
        }
        window.addEventListener("resize", setWindowHeightInCSS)
        setWindowHeightInCSS()
        return () => {
            window.removeEventListener("resize", setWindowHeightInCSS)
        }
    })

    const sbw = useScrollbarWidth()
    useEffect(() => {
        if (sbw) {
            document.documentElement.style.setProperty("--alter-scrollbar-width", `${sbw}px`)
        }
    }, [sbw])

    const components = [
        ReatomContextProvider,
        WrapBalanced.Provider,
        AppStatusProvider,
        ClientProvider,
        GrowthBookProvider,
    ].filter(Boolean) as JSXElementConstructor<PropsWithChildren<unknown>>[]

    return (
        <ErrorBoundary>
            <BrowserCheck>
                <Provider store={store}>
                    <AppQueryClientProvider>
                        <NotificationBanner />
                        <BrowserRouter>
                            <Compose components={components}>
                                <AppRouter />
                                <CallNotification />
                            </Compose>
                        </BrowserRouter>
                        {!isSSR &&
                            createPortal(
                                <ToastContainer
                                    hideProgressBar
                                    position={toast.POSITION.TOP_RIGHT}
                                    autoClose={4000}
                                    icon={false}
                                />,
                                document.body
                            )}
                    </AppQueryClientProvider>
                </Provider>
            </BrowserCheck>
        </ErrorBoundary>
    )
}

// SurveyResultsHandlerProvider нужно маунтить внутри App.js, иначе без перезагрузки
// страницы при возвращении из выбранного психолога в выдачу
// каждый раз будет отображаться новый список психологов.
// Сейчас для фикса бага https://app.clickup.com/t/25xrrdn использован хак в компоненте ProfileLink.tsx

// eslint-disable-next-line import/no-default-export
export default AppRoot
